import React from 'react';
import styled from 'styled-components';
import {Card, Icon} from "@blueprintjs/core";
import gql from 'graphql-tag';
import {useQuery} from "@apollo/client";
import moment from "moment-timezone";
import {useWindowDimensions} from "../../../hooks";
import {ScoreCard} from "../../../components/scorecard";

const SCHEDULE_QUERY = gql`
    query($teamId: Int!) {
        team(id: $teamId) {
            displayName: name(format: WITH_DIVISION_AND_MODIFIER)
        }

        gamesForTeam(id: $teamId) {
            id
            isCancelled

            createdBy {
                id
                name
            }
            opponent {
                id
                name
            }

            score {
                homeTeamRuns
                awayTeamRuns
            }

            schedulingDetails {
                homeTeam {
                    id
                    name(format: WITH_DIVISION_AND_MODIFIER)
                    displayName: name(format: WITH_DIVISION_AND_MODIFIER)
                }
                awayTeam {
                    id
                    name(format: WITH_DIVISION_AND_MODIFIER)
                    displayName: name(format: WITH_DIVISION_AND_MODIFIER) 
                }
                field {
                    id
                    name
                }
                dateTime
            }
            
            formHistory {
                id
                createdBy {
                    id
                    name
                }
                timeCreated
                approvalStatus

                ... on GameSchedulingForm {
                    gameDateTime
                    gameField {
                        id
                        name
                    }
                    isHomeGameForEnteringTeam
                }

                approvers {
                    team {
                        id
                        name
                    }
                    status
                    statusLastUpdated
                }
            }

            primaryUmpire {
                id
                firstName
                lastName
                phoneNumber
            }

            secondaryUmpire {
                id
                firstName
                lastName
                phoneNumber
            }

            details {
                homeTeamRecord
                awayTeamRecord
            }
        }
    }
`

const StyledCard = styled(Card)`
  position: relative;
  
  min-width: 400px;
  max-width: 800px;
  width: 100%;
  
  margin: 4px 0;
  padding: 15px;
  
  text-align: center;
`

const PageTitle = ({ teamName }) => {
  return (
    <StyledCard>
      <div
        style={{position: 'absolute', top: '10px', right: '10px', color: '#669EFF', cursor: 'pointer'}}
        onClick={() => window.print()}
      >
        <h3 style={{display: 'inline-block', textAlign: 'right', margin: 0, marginRight: '5px', padding: 0, fontWeight: '600'}}>Print Schedule</h3>
        <Icon icon={'arrow-right'} style={{marginBottom: '3px'}} />
      </div>
      <h1 style={{margin: 0, padding: 0}}>Games</h1>
      <h3 style={{margin: 0, padding: 0}}>{teamName}</h3>
    </StyledCard>
  )
}

export const TeamSchedule = ({ teamId }) => {

  const { loading, data } = useQuery(SCHEDULE_QUERY, {
    variables: {
      teamId
    }
  })

  if (loading) return ''

  const { displayName } = data.team
  const { gamesForTeam: games } = data

  return (
    <div style={{display: 'flex', justifyContent: 'center', position: 'relative'}}>
      <div style={{width: '100%'}}>
        <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
          <PageTitle teamName={displayName} />
        </div>

        <div style={{height: '25px'}} />

        <ol style={{margin: 0, padding: 0, width: '100%', listStyleType: 'none'}}>
          {
            games
              .filter(game => game.schedulingDetails)
              .sort((a, b) => moment(a.schedulingDetails.dateTime).isBefore(moment(b.schedulingDetails.dateTime)) ? -1 : 1 )
              .map(game => {
              return (
                <li key={game.id} style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                  <GameCard teamId={teamId} game={game} />
                </li>
              )
            })
          }
        </ol>
      </div>
    </div>
  )
}


const GameCardContent = styled.div`
  width: 100%;
  display: grid;

  grid-template-columns: 2fr 1fr;
  grid-template-areas: 'opponent details';

  @media only screen and (max-width: 800px) {
    grid-template-columns: 1fr;

    grid-template-areas:
      'details'
      'opponent';
  }
`

const GameCard = ({ teamId, game }) => {

  const { width } = useWindowDimensions()
  const isMobile = width < 800

  const { schedulingDetails } = game

  const isHomeTeam = parseInt(schedulingDetails.homeTeam.id) === teamId
  const opponentName = isHomeTeam ? schedulingDetails.awayTeam.displayName : schedulingDetails.homeTeam.displayName

  const now = new Date()
  now.setHours(now.getHours() - 1)

  return (
    <StyledCard interactive={true} style={{padding: '3px 8px 0 8px'}}>
      <GameCardContent>
        {isMobile ? (
            <>
              {(moment(game.dateTime).toDate() < now || game.score) ? (
                <div>
                  <div style={{gridArea: 'details', padding: '5px 5px 5px 13px'}}>
                    <GameLocationDetails isMobile={isMobile} game={game} />
                  </div>
                  <ScoreCard
                    stacked={false}
                    game={game.schedulingDetails}
                    score={game.score}
                    details={game.details}
                    isCancelled={game.isCancelled}
                  />
                </div>
              ) : (
                <div style={{position: 'relative'}}>
                  {game.isCancelled &&
                    <div
                      style={{position: 'absolute', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}
                    >
                      <h1
                        style={{textAlign: 'center'}}
                      >
                        <strong
                          style={{fontSize: '4rem', opacity: '0.25'}}
                        >CANCELLED</strong>
                      </h1>
                    </div>
                  }

                  <div style={{gridArea: 'details', padding: '5px 5px 5px 13px'}}>
                    <GameLocationDetails isMobile={isMobile} game={game} />
                  </div>
                  <div style={{gridArea: 'opponent', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '10px 0'}}>
                    <h2 style={{display: 'inline-block', margin: 0, padding: 0, textAlign: 'center'}}>{isHomeTeam ? 'VS' : '@'}&nbsp;&nbsp;&nbsp;{opponentName}</h2>
                  </div>
                </div>
              )}
            </>
        ) : (
          <>
            {(moment(game.dateTime).toDate() < now || game.score) ? (
              <div style={{borderRight: '1px solid lightgrey'}}>
                <ScoreCard
                  game={game.schedulingDetails}
                  score={game.score}
                  details={game.details}
                  isCancelled={game.isCancelled}
                />
              </div>
            ) : (
              <div style={{gridArea: 'opponent', borderRight: '1px dotted lightgrey', display: 'flex', position: 'relative'}}>
                {game.isCancelled &&
                  <div
                    style={{position: 'absolute', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}
                  >
                    <h1
                      style={{textAlign: 'center'}}
                    >
                      <strong
                        style={{fontSize: '4rem', opacity: '0.25'}}
                      >CANCELLED</strong>
                    </h1>
                  </div>
                }

                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '50px'}}>
                  <h2 style={{display: 'inline-block', margin: 0, padding: 0}}>{isHomeTeam ? 'VS' : '@'}&nbsp;</h2>
                </div>

                <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', width: 'calc(100% - 50px)'}}>
                  <h3 style={{display: 'inline-block', margin: 0, padding: 0, textAlign: 'center'}}>{opponentName}</h3>
                </div>
              </div>
            )}
            <div style={{gridArea: 'details', padding: '5px 5px 5px 13px'}}>
              <GameLocationDetails isMobile={isMobile} game={game} />
            </div>
          </>
        )}

      </GameCardContent>
    </StyledCard>
  )
}


const GameLocationDetails = ({ isMobile, game }) => {

  const { primaryUmpire: ump1, secondaryUmpire: ump2 } = game
  const { dateTime, field } = game.schedulingDetails

  const fieldName = field.name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());

  let umpires = 'Unknown'
  if (ump1 && ump2) {
    umpires = `${ump1.firstName} ${ump1.lastName} (${ump1.phoneNumber}) and ${ump2.firstName} ${ump2.lastName} (${ump2.phoneNumber})`
  } else if (ump1) {
    umpires = `${ump1.firstName} ${ump1.lastName} (${ump1.phoneNumber})`
  } else if (ump2) {
    umpires = `${ump2.firstName} ${ump2.lastName} (${ump2.phoneNumber})`
  }

  const formattedTimePart = moment(dateTime).format('h:mm a')

  return (
    <>
      {isMobile ? (
        <div>
          <h4 style={{margin: 0, padding: 0}}>{formattedTimePart} <span style={{color: 'grey'}}>&bull;</span> {moment(dateTime).format('LL')}</h4>
          <h4 style={{margin: 0, padding: 0}}>{fieldName}</h4>
          <h4 style={{margin: 0, padding: 0, marginTop: '3px'}}>-- Umpires --<br />{umpires}</h4>
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%'}}>
          <h4 style={{margin: 0, padding: 0}}>{formattedTimePart} <span style={{color: 'grey'}}>&bull;</span> {moment(dateTime).format('LL')}</h4>
          <h4 style={{padding: 0, margin: '5px 0'}}>at</h4>
          <h4 style={{margin: 0, padding: 0}}>{fieldName}</h4>
          <h4 style={{margin: 0, padding: 0, marginTop: '3px'}}>-- Umpires --<br />{umpires}</h4>
        </div>
      )}
    </>
  )
}
